import { useState } from 'react';
import type { Review } from '../../../mocks/movieDetails';

interface Props {
  reviews: Review[];
  overallRating: number;
}

export default function ReviewsSection({ reviews, overallRating }: Props) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? reviews : reviews.slice(0, 2);

  const bars = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: reviews.filter(r => Math.round(r.rating / 2) === star).length,
    pct: Math.round((reviews.filter(r => Math.round(r.rating / 2) === star).length / Math.max(reviews.length, 1)) * 100),
  }));

  return (
    <div>
      <h3 className="text-lg font-black text-gray-900 mb-6 flex items-center gap-2">
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-star-line text-[#C9A961]" />
        </div>
        تقييمات المشاهدين
      </h3>

      {/* Rating overview */}
      <div className="flex gap-8 items-center bg-[#FAFAF8] rounded-2xl p-6 mb-6">
        {/* Score */}
        <div className="text-center flex-shrink-0">
          <p className="text-5xl font-black text-[#C9A961]">{overallRating}</p>
          <div className="flex items-center justify-center gap-0.5 my-1.5">
            {[1,2,3,4,5].map(s => (
              <div key={s} className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-star-${s <= Math.round(overallRating / 2) ? 'fill' : 'line'} text-[#C9A961] text-xs`} />
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400">{(reviews.length * 847 + 1203).toLocaleString()} تقييم</p>
        </div>

        <div className="w-px h-20 bg-gray-200 flex-shrink-0" />

        {/* Bars */}
        <div className="flex-1 space-y-2">
          {bars.map(b => (
            <div key={b.star} className="flex items-center gap-2">
              <span className="text-xs font-bold text-gray-500 w-3">{b.star}</span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-star-fill text-[#C9A961] text-xs" />
              </div>
              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#C9A961] rounded-full transition-all duration-700"
                  style={{ width: `${b.pct}%` }}
                />
              </div>
              <span className="text-xs text-gray-400 w-8 text-left">{b.pct}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Review cards */}
      <div className="space-y-4">
        {visible.map((rev, i) => (
          <div key={i} className="bg-white rounded-2xl p-5 border border-gray-100">
            <div className="flex items-start gap-4">
              <img
                src={rev.avatar}
                alt={rev.author}
                className="w-11 h-11 rounded-full object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
                  <p className="font-black text-gray-900 text-sm">{rev.author}</p>
                  <span className="text-xs text-gray-400">{rev.date}</span>
                </div>
                {/* Stars */}
                <div className="flex items-center gap-0.5 mb-2">
                  {[1,2,3,4,5,6,7,8,9,10].map(s => (
                    <div key={s} className="w-3 h-3 flex items-center justify-center">
                      <i className={`ri-star-${s <= rev.rating ? 'fill' : 'line'} text-[#C9A961] text-[10px]`} />
                    </div>
                  ))}
                  <span className="text-[#C9A961] font-black text-xs mr-1">{rev.rating}/10</span>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{rev.text}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {reviews.length > 2 && (
        <button
          onClick={() => setShowAll(s => !s)}
          className="mt-4 w-full py-3 border border-gray-200 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors whitespace-nowrap"
        >
          {showAll ? 'عرض أقل' : `عرض جميع التقييمات (${reviews.length})`}
        </button>
      )}
    </div>
  );
}
